/**
 * SchedulersPanel - Disparo manual de jobs del scheduler (backfill sin cron)
 *
 * Cada botón llama a POST /api/admin/schedulers/run/{job_name}
 * Endpoint protegido por is_admin en el backend.
 */
import React, { useState } from 'react';
import {
  Calendar, TrendingUp, ShieldCheck, DollarSign,
  PlayCircle, CheckCircle2, XCircle, Loader2, Clock
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const JOBS = [
  {
    id: 'conclusion_snapshot',
    label: 'Veredictos Diarios',
    description: 'Puebla conclusion_history con veredictos alcistas/bajistas de HOY. Arregla el widget Cartera Veredicto si el cron nocturno falló.',
    cron: 'Todos los días 03:15 (España)',
    icon: Calendar,
    color: 'cyan',
    estimatedTime: '~15s'
  },
  {
    id: 'data_quality',
    label: 'Vigilante Calidad',
    description: 'Compara precios de HOY contra Yahoo Finance y detecta discrepancias. Log de calidad diario.',
    cron: 'Todos los días 22:30 (España)',
    icon: ShieldCheck,
    color: 'emerald',
    estimatedTime: '~30s'
  },
  {
    id: 'analyst_alerts',
    label: 'Alertas Analistas Tier-1',
    description: 'Escanea upgrades/downgrades de JP Morgan, Goldman, MS... en tickers de carteras de usuarios.',
    cron: 'Cada 4h',
    icon: TrendingUp,
    color: 'violet',
    estimatedTime: '~45s'
  },
  {
    id: 'dividends_snapshot',
    label: 'Snapshot Dividendos',
    description: 'Guarda el estado mensual de dividendos de la cartera para histórico y análisis.',
    cron: 'Día 1 de cada mes, 03:00 (España)',
    icon: DollarSign,
    color: 'amber',
    estimatedTime: '~10s'
  },
];

const COLOR_MAP = {
  cyan: 'text-gold-400 border-gold-500/40 hover:bg-gold-500/10',
  emerald: 'text-emerald-400 border-emerald-500/40 hover:bg-emerald-500/10',
  violet: 'text-zinc-400 border-zinc-500/40 hover:bg-zinc-500/10',
  amber: 'text-gold-400 border-gold-500/40 hover:bg-gold-500/10',
};

const SchedulersPanel = () => {
  // Estado por-job: null | 'running' | 'success' | 'error'
  const [status, setStatus] = useState({});
  const [results, setResults] = useState({});

  const runJob = async (jobId) => {
    setStatus((s) => ({ ...s, [jobId]: 'running' }));
    setResults((r) => ({ ...r, [jobId]: null }));

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/schedulers/run/${jobId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await res.json();

      if (!res.ok) {
        setStatus((s) => ({ ...s, [jobId]: 'error' }));
        setResults((r) => ({ ...r, [jobId]: data }));
        toast.error(`${jobId} falló: ${data.detail || 'Error desconocido'}`);
        return;
      }

      setStatus((s) => ({ ...s, [jobId]: 'success' }));
      setResults((r) => ({ ...r, [jobId]: data }));
      toast.success(`${jobId} completado ✓`);
    } catch (err) {
      setStatus((s) => ({ ...s, [jobId]: 'error' }));
      setResults((r) => ({ ...r, [jobId]: { detail: err.message } }));
      toast.error(`${jobId} error: ${err.message}`);
    }
  };

  const renderResult = (jobId) => {
    const r = results[jobId];
    if (!r) return null;

    // Job OK → mostrar detalle del result
    if (r.ok) {
      const result = r.result || {};
      const entries = Object.entries(result);
      return (
        <div className="mt-3 p-3 bg-emerald-950/30 border border-emerald-500/30 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            <span className="text-xs font-semibold text-emerald-300">
              Ejecutado por {r.triggered_by}
            </span>
          </div>
          {entries.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {entries.map(([k, v]) => (
                <div key={k} className="bg-slate-900/50 rounded px-2 py-1">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">{k}</div>
                  <div className="text-xs font-mono text-emerald-300">{JSON.stringify(v)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    // Error
    return (
      <div className="mt-3 p-3 bg-red-950/30 border border-red-500/30 rounded-lg">
        <div className="flex items-center gap-2">
          <XCircle className="w-4 h-4 text-red-400" />
          <span className="text-xs font-mono text-red-300 break-all">
            {r.detail || 'Error desconocido'}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-xl p-6">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-white mb-1">Schedulers · Disparo Manual</h2>
        <p className="text-sm text-slate-400">
          Ejecuta jobs del cron manualmente sin esperar a su horario programado.
          Útil como salvavidas si un cron nocturno falló o para backfillear datos.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {JOBS.map((job) => {
          const s = status[job.id];
          const Icon = job.icon;
          const colorClass = COLOR_MAP[job.color];

          return (
            <div
              key={job.id}
              className="bg-slate-900/50 border border-slate-700/70 rounded-xl p-5 hover:border-slate-600 transition-colors"
              data-testid={`scheduler-card-${job.id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg border ${colorClass}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">{job.label}</h3>
                    <div className="flex items-center gap-1.5 text-[11px] text-slate-500 mt-0.5">
                      <Clock className="w-3 h-3" />
                      <span>{job.cron}</span>
                      <span className="mx-1">·</span>
                      <span>{job.estimatedTime}</span>
                    </div>
                  </div>
                </div>
              </div>

              <p className="text-xs text-slate-400 mb-4 leading-relaxed">
                {job.description}
              </p>

              <button
                onClick={() => runJob(job.id)}
                disabled={s === 'running'}
                data-testid={`scheduler-run-${job.id}`}
                className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-colors border ${colorClass} disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {s === 'running' ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Ejecutando...
                  </>
                ) : s === 'success' ? (
                  <>
                    <PlayCircle className="w-4 h-4" />
                    Volver a ejecutar
                  </>
                ) : (
                  <>
                    <PlayCircle className="w-4 h-4" />
                    Ejecutar ahora
                  </>
                )}
              </button>

              {renderResult(job.id)}
            </div>
          );
        })}
      </div>

      <div className="mt-6 p-4 bg-gold-950/20 border border-gold-500/20 rounded-lg">
        <p className="text-xs text-gold-300/90 leading-relaxed">
          <strong>Nota:</strong> estos jobs pueden tardar de 10s a varios minutos.
          El endpoint es asíncrono: la petición se queda esperando hasta que termine el job
          y devuelve el resumen de resultados. No cierres esta pestaña mientras se ejecuta.
        </p>
      </div>
    </div>
  );
};

export default SchedulersPanel;
